import { useEffect, useLayoutEffect, useRef } from 'react';
import type { AllocationDraft } from '../fields/allocation.js';
import type { CheckoutIo } from '../model/checkout_env.js';
import type { CheckoutBudget, CheckoutProductType } from '../model/checkout_source.js';

export interface UseBudgetAdvisoryOptions {
  /** The host's reads; `null` ⇒ no lighthouse. */
  io: CheckoutIo | null;
  /** Cerca do chamador (drawer aberto + form pronto + há alocação a consultar).
   *  `false` ⇒ nenhuma consulta, e o resultado anterior é LIMPO: um farol de
   *  quando a cerca valia não pode sobreviver a ela cair (ex.: transição viva
   *  para um modo sem advisory). */
  enabled: boolean;
  productType: CheckoutProductType;
  /** As linhas do rateio: cada uma consulta o saldo do seu par empresa/centro de
   *  custo pela sua parte do total (preço × percentual, #6697). */
  rows: readonly AllocationDraft[];
  /** Total product-aware do carrinho — é o que o servidor compara com o saldo. */
  total: number;
  /** Início da estadia/locação/viagem; ausente ⇒ hoje. */
  startDate?: string | undefined;
  /** Estáveis (useCallback) — entram nas deps do efeito. */
  onBudget: (budgets: readonly (CheckoutBudget | undefined)[]) => void;
  onClear: () => void;
}

/** A parte de uma linha no total, em centavos inteiros: uma linha sozinha (sem
 *  percentual) leva o total inteiro. */
function rowValue(row: AllocationDraft, total: number): number {
  return Math.round(total * (row.percent ?? 100)) / 100;
}

/** A chave das consultas: muda só quando um par ou uma parte muda — o array novo
 *  de cada render não pode refazer as consultas. */
function rowsKey(rows: readonly AllocationDraft[], total: number): string {
  return rows
    .map((row) =>
      row.companyId && row.costCenterId
        ? `${row.companyId}|${row.costCenterId}|${String(rowValue(row, total))}`
        : '',
    )
    .join(',');
}

/** O saldo de uma linha pela sua parte do total. Linha sem empresa OU sem
 *  centro de custo não consulta; budget is non-blocking — a linha que falha
 *  fica sem saldo. */
function rowBudget(
  port: CheckoutIo,
  productType: CheckoutProductType,
  row: AllocationDraft,
  total: number,
  startDate: string,
): Promise<CheckoutBudget | undefined> {
  if (!row.companyId || !row.costCenterId) return Promise.resolve(undefined);
  return port
    .getBudget(productType, {
      value: rowValue(row, total),
      companyId: row.companyId,
      costCenterId: row.costCenterId,
      startDate,
    })
    .catch(() => undefined);
}

/** Advisory de orçamento (o farol): re-consulta o saldo de cada linha do rateio
 *  com debounce de ~300ms, cancelando a consulta em voo na troca. Linha sem
 *  empresa OU sem centro de custo fica sem saldo; nenhuma linha completa limpa o
 *  farol sem consultar. Falha é engolida — o advisory nunca bloqueia o submit (o
 *  servidor decide). Compartilhado pelos dois caminhos de checkout (single e
 *  multi no modo requester). */
export function useBudgetAdvisory({
  io: port,
  enabled,
  productType,
  rows,
  total,
  startDate,
  onBudget,
  onClear,
}: UseBudgetAdvisoryOptions): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const key = rowsKey(rows, total);
  // As linhas do último commit, lidas pelo timer: escritas num layout effect, nunca no render.
  const rowsRef = useRef(rows);
  useLayoutEffect(() => {
    rowsRef.current = rows;
  });

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!enabled || !port || key.split(',').every((part) => part === '')) {
      onClear();
      return undefined;
    }
    // Limpa o resultado ANTERIOR antes de agendar a consulta nova: sem isso, o
    // saldo do par antigo fica na tela pareado com o NOME do par novo durante
    // debounce+RTT — um aviso mentindo sobre qual rateio ele descreve. No
    // primeiro run é no-op (não há resultado ainda).
    onClear();
    let cancelled = false;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const effectiveStartDate = startDate ?? new Date().toISOString().slice(0, 10);
      void Promise.all(
        rowsRef.current.map((row) => rowBudget(port, productType, row, total, effectiveStartDate)),
      ).then((budgets) => {
        if (!cancelled) onBudget(budgets);
      });
    }, 300);
    return () => {
      cancelled = true;
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, key, total, startDate, productType, port, onBudget, onClear]);
}
